import "./bill-hold-modal.css";

import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import type { Dispatch, SetStateAction } from "react";
import { useId, useState } from "react";
import { useParams } from "react-router";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostBillHold } from "../../hooks/use-post-bill-hold.ts";
import type { AugmentedShipment } from "../../types/augmented-shipment";
import type { FormErrors, FormValues } from "./utils/validation.ts";
import { INITIAL_VALUES, validate } from "./utils/validation.ts";

const REASON_OPTIONS = [
	{ id: 1, value: "Waiting on Customer PO" },
	{ id: 2, value: "Waiting on a Claim" },
	{ id: 3, value: "Need Approval for Detention" },
	{ id: 4, value: "Need Approval for Layover" },
	{ id: 5, value: "Other" },
];

type BillHoldFormIds = {
	categoryId: string;
	categoryErrorId: string;
	reasonId: string;
	errorId: string;
	followupDateId: string;
	followupDateErrorId: string;
};

const BillHoldForm = ({
	values,
	setValues,
	errors,
	submitted,
	ids,
}: {
	values: FormValues;
	setValues: Dispatch<SetStateAction<FormValues>>;
	errors: FormErrors;
	submitted: boolean;
	ids: BillHoldFormIds;
}) => {
	// The free-text reason only applies when the "Other" category is selected.
	const isOther = values.category === "Other";
	const showFollowupError = submitted && Boolean(errors.followupDate);
	const showReasonError = submitted && Boolean(errors.reason);
	const showCategoryError = submitted && Boolean(errors.category);

	return (
		<div className={"bill-hold-form"}>
			<div className={"bill-hold-field bill-hold-category"}>
				<Label editorId={ids.categoryId} id={`${ids.categoryId}_label`}>
					Select One
					<span className={"required"}>&nbsp;*</span>
				</Label>
				<DropDownList
					ariaDescribedBy={showCategoryError ? ids.categoryErrorId : undefined}
					ariaLabelledBy={`${ids.categoryId}_label`}
					data={REASON_OPTIONS}
					dataItemKey={"id"}
					id={ids.categoryId}
					onChange={(event: { value: { id: number; value: string } }) =>
						setValues((prev) => ({
							...prev,
							category: event.value.value,
							// Clear any stale free-text reason when a non-"Other"
							// category is chosen — the dropdown value is the reason.
							reason: event.value.value === "Other" ? prev.reason : "",
						}))
					}
					textField={"value"}
					valid={!showCategoryError}
					value={
						REASON_OPTIONS.find((option) => option.value === values.category) ??
						null
					}
				/>
				{showCategoryError ? (
					<Error id={ids.categoryErrorId}>{errors.category}</Error>
				) : null}
			</div>

			{isOther ? (
				<div className={"bill-hold-field"}>
					<Label editorId={ids.reasonId}>
						Reason
						<span className={"required"}>&nbsp;*</span>
					</Label>
					<Input
						ariaDescribedBy={showReasonError ? ids.errorId : undefined}
						id={ids.reasonId}
						onChange={(event: { value: string }) =>
							setValues((prev) => ({
								...prev,
								reason: event.value,
							}))
						}
						valid={!showReasonError}
						value={values.reason}
					/>
					{showReasonError ? (
						<Error id={ids.errorId}>{errors.reason}</Error>
					) : null}
				</div>
			) : null}

			<div className={"bill-hold-field bill-hold-date"}>
				<Label editorId={ids.followupDateId}>
					Estimated Date for Customer Billing
					<span className={"required"}>&nbsp;*</span>
				</Label>
				<DatePicker
					ariaDescribedBy={
						showFollowupError ? ids.followupDateErrorId : undefined
					}
					id={ids.followupDateId}
					onChange={(event) =>
						setValues((prev) => ({
							...prev,
							followupDate: event.target.value,
						}))
					}
					valid={!showFollowupError}
					value={values.followupDate}
				/>
				{showFollowupError ? (
					<Error id={ids.followupDateErrorId}>{errors.followupDate}</Error>
				) : null}
			</div>

			<div className={"bill-hold-confirmation-text"}>
				<p>
					Are you sure you want to send Carrier Payment request to Accounting
					and Hold the Customer Billing?
				</p>
				<p>Once confirmed, certain Load Details will be locked.</p>
			</div>
		</div>
	);
};

export const BillHoldModal = ({
	onClose,
	isOpen,
	shipment,
}: {
	onClose: () => void;
	isOpen: boolean;
	shipment: AugmentedShipment;
}) => {
	const reasonId = useId();
	const categoryId = useId();
	const followupDateId = useId();
	const params = useParams();
	const { showSuccess, showError } = useShipmentNotifications();
	const { billHoldAsync, isSavingBillHold } = usePostBillHold();
	const errorId = `${reasonId}_error`;
	const categoryErrorId = `${categoryId}_error`;
	const followupDateErrorId = `${followupDateId}_error`;

	const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
	const [submitted, setSubmitted] = useState(false);

	// The free-text reason only applies when the "Other" category is selected.
	const isOther = values.category === "Other";

	// Derived during render — single source of truth, no stale error state.
	const errors = validate(values);
	const isValid = !errors.category && !errors.reason && !errors.followupDate;

	// Returning null doesn't unmount us, so reset state on every close path.
	const handleClose = () => {
		setValues(INITIAL_VALUES);
		setSubmitted(false);
		onClose();
	};

	const handleConfirm = async () => {
		setSubmitted(true);
		const { followupDate } = values;
		if (!isValid || !followupDate) {
			return;
		}
		try {
			await billHoldAsync({
				poNumber: Number(params["poNumber"] ?? 0),
				customerId: shipment?.customer?.id ?? 0,
				reason: isOther ? values.reason : values.category,
				estimatedDate: followupDate.toISOString(),
			});
		} catch {
			showError("Unable to create Bill Hold. Please try again.");
			return;
		}
		showSuccess("Bill Hold created successfully.");
		handleClose();
	};

	const isSaving = isSavingBillHold;

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			className={"bill-hold-modal"}
			onCancel={handleClose}
			onClose={handleClose}
			onConfirm={() => void handleConfirm()}
			showButtonsDisabled={isSaving}
			title={`Bill Holding Details`}
			type={"confirmation"}
			width={397}
		>
			{isSaving ? (
				<div className={"flag-loader"}>
					<Loader
						size={"large"}
						themeColor={"primary"}
						type={"infinite-spinner"}
					/>
				</div>
			) : (
				<BillHoldForm
					errors={errors}
					ids={{
						categoryId,
						categoryErrorId,
						reasonId,
						errorId,
						followupDateId,
						followupDateErrorId,
					}}
					setValues={setValues}
					submitted={submitted}
					values={values}
				/>
			)}
		</TraxModal>
	);
};
